import { useEffect } from "react";
import { motion } from "framer-motion";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { WhatsAppFab } from "@/components/landing/WhatsAppFab";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { PatternLayer, patternLight } from "@/components/landing/patterns";
import { IMAGES, SITE } from "@/lib/site";
import { useSEO } from "@/lib/seo";
import { useGo } from "@/lib/nav";
import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle, Eye, Scale, Banknote, MapPin, Phone } from "lucide-react";

const categories = [
  {
    key: "gold",
    title: "Sell Gold",
    img: IMAGES.sellGold,
    alt: "Customer's gold jewelry being weighed on a scale at the counter",
    desc: "10k, 14k and 18k jewelry, coins, bullion and broken pieces — tested and weighed right in front of you, and paid on the spot.",
    wa: "Hello Gold Time, I'd like to sell some gold.",
  },
  {
    key: "watches",
    title: "Sell Watches",
    img: IMAGES.sellWatches,
    alt: "A luxury watch being appraised at the counter with a loupe",
    desc: "Luxury and mid-range timepieces — Rolex, Omega, Cartier and more. Authenticated by our team with fair market offers and immediate payment. Bring box and papers if you have them.",
    wa: "Hello Gold Time, I'd like to sell a watch.",
    external: true,
  },
  {
    key: "diamonds",
    title: "Sell Diamonds",
    img: IMAGES.sellDiamonds,
    alt: "A diamond ring being examined with a jeweler's loupe",
    desc: "Loose stones and diamond jewelry, certified or not. Evaluated honestly with the grading explained step by step — you always know exactly what you have and what it's worth.",
    wa: "Hello Gold Time, I'd like to sell diamonds.",
  },
];

const steps = [
  { icon: Eye, title: "Watch every test", text: "Your items are tested and weighed on the counter, in front of you." },
  { icon: Scale, title: "Honest valuation", text: "Transparent pricing based on the live market rate, explained clearly." },
  { icon: Banknote, title: "Paid on the spot", text: "Accept the offer and walk out with payment the same visit." },
];

export default function SellPage() {
  const go = useGo();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useSEO({
    title: "Sell Your Gold, Diamonds & Watches | Gold Time Jewelry",
    canonicalPath: "/sell",
    description: "Sell gold, diamonds, watches and estate jewelry to Gold Time Jewelry — tested and weighed in front of you, honest offers, paid on the spot. Three Texas showrooms.",
  });

  return (
    <main data-testid="sell-page">
      <Header />

      <section className="relative overflow-hidden pt-[164px] pb-20 lg:pt-44 lg:pb-28" data-testid="sell-hero">
        <img
          src="/assets/texas-glow.jpg"
          alt=""
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] lg:w-[1400px] opacity-20 pointer-events-none select-none [mask-image:radial-gradient(ellipse_at_center,black_35%,transparent_72%)]"
        />
        <PatternLayer pattern={patternLight} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 max-w-3xl lg:mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-xs md:text-sm uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-6 block"
          >
            Sell To Us
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            data-testid="sell-hero-heading"
            className="font-display text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight text-neutral-900 leading-[1.1]"
          >
            Turn your valuables into <span className="text-[#C9A227]">top-dollar offers</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 text-base md:text-lg leading-relaxed text-neutral-600"
          >
            Gold, watches and diamonds — every item is evaluated honestly, in front of you, at any of
            our three Texas showrooms. No pressure, no obligation.
          </motion.p>
        </div>
      </section>

      <section className="pb-24 lg:pb-28" data-testid="sell-categories">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {categories.map((c, i) => (
              <Reveal key={c.key} delay={0.08 * i}>
                <div
                  data-testid={`sell-card-${c.key}`}
                  className="group h-full bg-white border border-[#E8DCC4] rounded-sm overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(201,162,39,0.10)] transition-shadow duration-500 flex flex-col"
                >
                  <div className="overflow-hidden">
                    <img
                      src={c.img}
                      alt={c.alt}
                      className="w-full h-[280px] lg:h-[320px] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                  </div>
                  <div className="p-6 lg:p-8 flex flex-col flex-1">
                    <h2 className="font-display text-2xl text-neutral-900">{c.title}</h2>
                    <p className="text-sm text-neutral-600 mt-3 leading-relaxed flex-1">{c.desc}</p>
                    {c.external ? (
                      <div className="mt-6 space-y-3">
                        <a
                          href={SITE.watchBuyersUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`sell-cta-${c.key}`}
                          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-6 py-3 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_16px_32px_rgb(201,162,39,0.20)]"
                        >
                          Get a watch valuation online <ArrowUpRight size={16} strokeWidth={1.5} />
                        </a>
                        <p className="text-xs text-neutral-500">
                          Swiss watch? Our sister site{" "}
                          <span className="font-semibold text-neutral-700">{SITE.watchBuyersName}</span> specializes in
                          Rolex, Omega, Patek Philippe &amp; more — nationwide.
                        </p>
                        <a
                          href={`${SITE.whatsappHref}?text=${encodeURIComponent(c.wa)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`sell-whatsapp-${c.key}`}
                          className="inline-flex items-center gap-2 text-sm font-semibold text-[#C9A227] hover:text-[#A8871B] transition-colors duration-300"
                        >
                          Or message a showroom <ArrowUpRight size={16} strokeWidth={1.5} />
                        </a>
                      </div>
                    ) : (
                      <div className="mt-6 space-y-3">
                        <Link
                          to={c.key === "gold" ? "/sell-gold" : "/sell-diamonds"}
                          data-testid={`sell-cta-${c.key}`}
                          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-6 py-3 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_16px_32px_rgb(201,162,39,0.20)]"
                        >
                          See how it works <ArrowUpRight size={16} strokeWidth={1.5} />
                        </Link>
                        <div>
                          <a
                            href={`${SITE.whatsappHref}?text=${encodeURIComponent(c.wa)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`sell-whatsapp-${c.key}`}
                            className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-500 hover:text-[#C9A227] transition-colors duration-300"
                          >
                            Prefer chat? WhatsApp us <ArrowUpRight size={13} strokeWidth={1.5} />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-[#F5F3EC] border-y border-[#E8DCC4]" data-testid="sell-steps">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="max-w-2xl">
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-neutral-900" data-testid="sell-steps-heading">
              Three steps, <span className="text-[#C9A227]">fifteen minutes</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-3 gap-8">
            {steps.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={0.08 * i}>
                <div className="flex items-start gap-5" data-testid={`sell-step-${i}`}>
                  <div className="w-12 h-12 shrink-0 rounded-full bg-white border border-[#E8DCC4] flex items-center justify-center">
                    <Icon size={20} strokeWidth={1.25} className="text-[#C9A227]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-800">{title}</h3>
                    <p className="text-sm text-neutral-600 mt-1 leading-relaxed">{text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24" data-testid="sell-visit">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-neutral-900" data-testid="sell-visit-heading">
              Prefer to talk <span className="text-[#C9A227]">in person?</span>
            </h2>
            <p className="mt-4 text-base md:text-lg text-neutral-600 max-w-xl mx-auto">
              Walk into any showroom — no appointment needed. Or message us first and we'll have an
              offer ready to discuss.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <a
                href={SITE.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sell-visit-whatsapp"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-8 py-3.5 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_20px_40px_rgb(201,162,39,0.20)]"
              >
                <MessageCircle size={16} strokeWidth={1.5} /> WhatsApp us
              </a>
              <a
                href="/#locations"
                onClick={go("/#locations")}
                data-testid="sell-visit-showrooms"
                className="inline-flex items-center gap-2 rounded-full border border-[#C9A227] text-[#C9A227] text-sm font-semibold px-8 py-3.5 hover:bg-[#F5F3EC] transition-colors duration-300"
              >
                <MapPin size={16} strokeWidth={1.5} /> Visit a showroom
              </a>
              <a
                href={SITE.phoneHref}
                data-testid="sell-visit-call"
                className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-700 hover:text-[#C9A227] transition-colors duration-300"
              >
                <Phone size={16} strokeWidth={1.5} /> {SITE.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
      <WhatsAppFab />
    </main>
  );
}
